import { forwardRef } from "react";
import { PERSONAS } from "../data/org";
import { initials, monogramColor } from "../lib/monogram";
import { useRH } from "../store/rh";

export interface MeetingPosterProps {
  title: string;
  topic: string;
  mode: "ata" | "conselho";
  agenda: string[];
  personaIds: string[];
}

export const MeetingPoster = forwardRef<HTMLDivElement, MeetingPosterProps>(
  function MeetingPoster({ title, topic, mode, agenda, personaIds }, ref) {

    const employees = useRH(s => s.employees);
    const personas = personaIds
      .map(id => PERSONAS.find(p => p.id === id))
      .filter((p): p is NonNullable<typeof p> => Boolean(p));

    return (
      <div
        ref={ref}
        style={{
          width: 1080,
          minHeight: 1350,
          background: "#F2EEE6",
          color: "#121214",
          padding: 60,
          fontFamily: "'JetBrains Mono', monospace"
        }}
      >
        {/* TOP RULE */}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>
          <span>AVPC — REUNIÃO · 01</span>
          <span style={{ opacity: 0.5 }}>{mode === "conselho" ? "conselho ao marcio" : "ata oficial"}</span>
          <span>MMXXVI</span>
        </div>
        <div style={{ borderTop: "2px solid #121214", marginTop: 6 }} />

        {/* TITLE */}
        <div style={{ textAlign: "center", marginTop: 36 }}>
          <h1 style={{
            fontFamily: "'Big Shoulders Display', sans-serif",
            fontWeight: 900,
            fontSize: 140,
            lineHeight: 0.85,
            letterSpacing: -1,
            textTransform: "uppercase",
            margin: 0
          }}>
            {title || topic}
          </h1>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
            <span style={{ width: 12, height: 12, background: mode === "conselho" ? "#A8863C" : "#BC2A24", display: "inline-block" }} />
          </div>
          <p style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 28,
            marginTop: 14,
            color: "#3C3A36"
          }}>
            {mode === "conselho" ? "conselho ao marcio" : "ata de reunião"} · {topic}
          </p>
        </div>

        <div style={{ borderTop: "2px solid #121214", marginTop: 24 }} />

        {/* MESA */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 0", fontSize: 11, textTransform: "uppercase", letterSpacing: 1
        }}>
          <span>01 · mesa convocada</span>
          <span style={{ opacity: 0.5 }}>{personas.length} participantes</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
          {personas.map(p => {
            const av = employees[p.id]?.avatarUrl;
            return (
              <div key={p.id} style={{
                border: "2px solid #121214",
                background: "#F2EEE6",
                padding: 8,
                display: "flex",
                alignItems: "center",
                gap: 8
              }}>
                <div style={{
                  width: 44, height: 44, background: monogramColor(p.id),
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 900,
                  fontSize: 16, color: "#F2EEE6", overflow: "hidden", flexShrink: 0
                }}>
                  {av
                    ? <img src={av} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : initials(p.name)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Big Shoulders Display', sans-serif", fontWeight: 700,
                    fontSize: 14, textTransform: "uppercase", lineHeight: 1
                  }}>
                    {p.name}
                  </div>
                  <div style={{
                    fontFamily: "'Crimson Pro', serif", fontStyle: "italic",
                    fontSize: 11, color: "#3C3A36",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                  }}>
                    {p.bio}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ borderTop: "2px solid #121214" }} />
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 0", fontSize: 11, textTransform: "uppercase", letterSpacing: 1
        }}>
          <span>02 · {mode === "conselho" ? "conselho da mesa" : "ata"}</span>
          <span style={{ opacity: 0.5 }}>{agenda.length} linhas</span>
        </div>

        {/* AGENDA */}
        <div style={{
          border: "2px solid #121214",
          background: "#F2EEE6",
          padding: 22,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 14,
          lineHeight: 1.55,
          whiteSpace: "pre-wrap"
        }}>
          {agenda.join("\n")}
        </div>

        {/* FOOTER */}
        <div style={{ marginTop: 32, borderTop: "2px solid #121214" }} />
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8,
          padding: "14px 0", fontSize: 11, textTransform: "uppercase", letterSpacing: 1
        }}>
          <span>VPC · AGÊNCIA</span>
          <span>R3 GROUP LLC</span>
          <span style={{ textAlign: "center" }}>MMXXVI</span>
          <span style={{ textAlign: "right" }}>ATA Nº 01</span>
        </div>
        <div style={{ borderTop: "2px solid #121214" }} />
        <p style={{
          fontFamily: "'Instrument Serif', serif", fontStyle: "italic",
          color: "#3C3A36", textAlign: "center", marginTop: 16, fontSize: 16
        }}>
          “Estrutura de holding premium — operação de agência, infoproduto e fintech sob um mesmo comando.”
        </p>
      </div>
    );
  }
);
